import React, { useCallback, useEffect, useState } from 'react';
import { useAdmin, ADMIN_DATA_CHANGED_EVENT } from 'src/admin';
import { getAdminConfigApi, putAdminConfigApi, type AdminConfig } from 'src/services/api';
import { invalidateAppConfigCache } from 'src/services/appConfigService';
import { useAppConfig } from 'src/components/web/AppConfigContext';

const STORAGE_USER = 'flower_power_notes_user';

const emptyConfig: AdminConfig = {
  pricingMessage: '',
  supportEmail: '',
  appDisplayName: '',
  termsUrl: '',
  termsContent: '',
  privacyUrl: '',
  privacyContent: '',
  helpUrl: '',
  helpContent: '',
  signupEnabled: '',
  maintenanceMode: '',
  maintenanceMessage: '',
  loginBanner: '',
  registerSuccessMessage: '',
  subscriptionSuccessMessage: '',
  priceLabel: '',
  sessionDurationDays: '',
  notificationTitlePrefix: '',
  emailSendingEnabled: '',
};

const AdminSettings: React.FC = () => {
  const { refresh: refreshAppConfig } = useAppConfig();
  const { refreshAdminData } = useAdmin();
  const [config, setConfig] = useState<AdminConfig>(emptyConfig);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const getAuthToken = () => {
    const raw = localStorage.getItem(STORAGE_USER);
    const user = raw ? JSON.parse(raw) : {};
    return user?.AuthToken ?? '';
  };

  const load = useCallback(async () => {
    setLoading(true);
    const result = await getAdminConfigApi(getAuthToken());
    setLoading(false);
    if (result.error) {
      setError(result.error);
      return;
    }
    if (result.config) {
      setConfig({ ...emptyConfig, ...result.config });
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    const handler = () => load();
    window.addEventListener(ADMIN_DATA_CHANGED_EVENT, handler);
    return () => window.removeEventListener(ADMIN_DATA_CHANGED_EVENT, handler);
  }, [load]);

  const update = (key: keyof AdminConfig, value: string) => {
    setConfig((c) => ({ ...c, [key]: value }));
  };

  const handleSave = async () => {
    setError(null);
    setSaving(true);
    const result = await putAdminConfigApi(getAuthToken(), config);
    setSaving(false);
    if (result.error) {
      setError(result.error);
      return;
    }
    invalidateAppConfigCache();
    await refreshAppConfig();
    await refreshAdminData();
    setSaved(true);
    setTimeout(() => setSaved(false), 2000);
  };

  const inputClass =
    'px-2 py-1.5 border border-gray-300 focus:border-sky-500 rounded w-full text-xs focus:ring-1 focus:ring-sky-500';
  const labelClass = 'block font-bold text-gray-700 text-xs';
  const hintClass = 'mt-0.5 mb-1 text-gray-500 text-xs';
  const sectionClass = 'bg-white p-3 border border-gray-200 rounded-lg';

  if (loading) {
    return (
      <div className="flex flex-col flex-1 p-1.5 w-full min-h-0 overflow-hidden">
        <div className="flex items-center gap-2 shrink-0">
          <h1 className="font-semibold text-gray-900 text-sm">Settings</h1>
          <span className="text-gray-500 text-xs">Loading…</span>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col flex-1 p-1.5 w-full min-h-0 overflow-hidden">
      <header className="flex flex-wrap justify-between items-center gap-1 mb-2 shrink-0">
        <h1 className="font-semibold text-gray-900 text-sm">Settings</h1>
        <div className="flex items-center gap-1">
          <button
            type="button"
            onClick={handleSave}
            disabled={saving}
            className="bg-sky-600 hover:bg-sky-700 disabled:opacity-50 px-2.5 py-1.5 rounded font-medium text-white text-xs"
          >
            {saving ? '…' : 'Save all'}
          </button>
          {saved && <span className="text-green-600 text-xs">Saved.</span>}
        </div>
      </header>

      <div className="flex-1 space-y-3 pb-1 min-h-0 overflow-auto">
        {error && <p className="text-red-600 text-xs shrink-0">{error}</p>}

        <section className={sectionClass}>
          <h2 className="mb-2 font-semibold text-gray-900 text-sm">Branding</h2>
          <div className="space-y-3 max-w-xl">
            <div>
              <label htmlFor="app-display-name" className={labelClass}>App display name</label>
              <p className={hintClass}>Shown in header, emails, and titles. Leave empty for default.</p>
              <input
                id="app-display-name"
                type="text"
                value={config.appDisplayName}
                onChange={(e) => update('appDisplayName', e.target.value)}
                className={inputClass}
                placeholder="e.g. LinkedIn Notes"
              />
            </div>
            <div>
              <label htmlFor="support-email" className={labelClass}>Support email</label>
              <p className={hintClass}>Shown for “Contact support” and in footer.</p>
              <input
                id="support-email"
                type="email"
                value={config.supportEmail}
                onChange={(e) => update('supportEmail', e.target.value)}
                className={inputClass}
                placeholder="support@example.com"
              />
            </div>
            <div>
              <label htmlFor="terms-url" className={labelClass}>Terms of service URL</label>
              <p className={hintClass}>If set, /terms redirects here and written content below is not used. If empty, the in-app page shows the written content.</p>
              <input
                id="terms-url"
                type="url"
                value={config.termsUrl}
                onChange={(e) => update('termsUrl', e.target.value)}
                className={inputClass}
                placeholder="https://..."
              />
            </div>
            <div>
              <label htmlFor="privacy-url" className={labelClass}>Privacy policy URL</label>
              <p className={hintClass}>If set, /privacy redirects here and written content below is not used. If empty, the in-app page shows the written content.</p>
              <input
                id="privacy-url"
                type="url"
                value={config.privacyUrl}
                onChange={(e) => update('privacyUrl', e.target.value)}
                className={inputClass}
                placeholder="https://..."
              />
            </div>
            <div>
              <label htmlFor="help-url" className={labelClass}>Help / docs URL</label>
              <p className={hintClass}>If set, /help redirects here and written content below is not used. If empty, the in-app page shows the written content.</p>
              <input
                id="help-url"
                type="url"
                value={config.helpUrl}
                onChange={(e) => update('helpUrl', e.target.value)}
                className={inputClass}
                placeholder="https://..."
              />
            </div>
            <div>
              <label htmlFor="terms-content" className={labelClass}>Terms of Service (in-app page)</label>
              <p className={hintClass}>Shown on /terms when Terms URL empty.</p>
              <textarea
                id="terms-content"
                rows={4}
                value={config.termsContent}
                onChange={(e) => update('termsContent', e.target.value)}
                className={inputClass}
                placeholder="e.g. By using this service you agree to..."
              />
            </div>
            <div>
              <label htmlFor="privacy-content" className={labelClass}>Privacy Policy (in-app page)</label>
              <p className={hintClass}>Shown on /privacy when Privacy URL empty.</p>
              <textarea
                id="privacy-content"
                rows={4}
                value={config.privacyContent}
                onChange={(e) => update('privacyContent', e.target.value)}
                className={inputClass}
                placeholder="e.g. We collect and use your data..."
              />
            </div>
            <div>
              <label htmlFor="help-content" className={labelClass}>Help / docs (in-app page)</label>
              <p className={hintClass}>Shown on /help when Help URL empty.</p>
              <textarea
                id="help-content"
                rows={4}
                value={config.helpContent}
                onChange={(e) => update('helpContent', e.target.value)}
                className={inputClass}
                placeholder="e.g. How to get started, FAQ..."
              />
            </div>
          </div>
        </section>

        <section className={sectionClass}>
          <h2 className="mb-2 font-semibold text-gray-900 text-sm">Access</h2>
          <div className="space-y-3 max-w-xl">
            <div className="flex items-center gap-2">
              <input
                id="signup-enabled"
                type="checkbox"
                checked={config.signupEnabled !== 'false'}
                onChange={(e) => update('signupEnabled', e.target.checked ? 'true' : 'false')}
                className="border-gray-300 rounded focus:ring-sky-500 text-sky-600"
              />
              <label htmlFor="signup-enabled" className={labelClass}>Signup enabled</label>
            </div>
            <p className={hintClass}>When off, new users cannot register.</p>
            <div className="flex items-center gap-2">
              <input
                id="maintenance-mode"
                type="checkbox"
                checked={config.maintenanceMode === 'true'}
                onChange={(e) => update('maintenanceMode', e.target.checked ? 'true' : 'false')}
                className="border-gray-300 rounded focus:ring-sky-500 text-sky-600"
              />
              <label htmlFor="maintenance-mode" className={labelClass}>Maintenance mode</label>
            </div>
            <p className={hintClass}>When on, show a global maintenance message. Set the message below.</p>
            <div>
              <label htmlFor="maintenance-message" className={labelClass}>Maintenance message</label>
              <textarea
                id="maintenance-message"
                rows={1}
                value={config.maintenanceMessage}
                onChange={(e) => update('maintenanceMessage', e.target.value)}
                className={inputClass}
                placeholder="We'll be back shortly."
              />
            </div>
            <div>
              <label htmlFor="session-duration-days" className={labelClass}>Session duration (days)</label>
              <p className={hintClass}>How long a login session lasts. Leave empty for default (e.g. 30).</p>
              <input
                id="session-duration-days"
                type="number"
                min={1}
                value={config.sessionDurationDays}
                onChange={(e) => update('sessionDurationDays', e.target.value)}
                className={inputClass}
                placeholder="30"
              />
            </div>
          </div>
        </section>

        <section className={sectionClass}>
          <h2 className="mb-2 font-semibold text-gray-900 text-sm">Copy – Login & Signup</h2>
          <div className="space-y-3 max-w-xl">
            <div>
              <label htmlFor="login-banner" className={labelClass}>Login banner</label>
              <textarea
                id="login-banner"
                rows={1}
                value={config.loginBanner}
                onChange={(e) => update('loginBanner', e.target.value)}
                className={inputClass}
                placeholder="e.g. Scheduled maintenance tonight 2–4 AM."
              />
            </div>
            <div>
              <label htmlFor="register-success-message" className={labelClass}>Register success message</label>
              <textarea
                id="register-success-message"
                rows={1}
                value={config.registerSuccessMessage}
                onChange={(e) => update('registerSuccessMessage', e.target.value)}
                className={inputClass}
              />
            </div>
          </div>
        </section>

        <section className={sectionClass}>
          <h2 className="mb-2 font-semibold text-gray-900 text-sm">Copy – Pricing & Billing</h2>
          <div className="space-y-3 max-w-xl">
            <div>
              <label htmlFor="pricing-message" className={labelClass}>Pricing message</label>
              <textarea
                id="pricing-message"
                rows={2}
                value={config.pricingMessage}
                onChange={(e) => update('pricingMessage', e.target.value)}
                className={inputClass}
                placeholder="e.g. Special offer this month..."
              />
            </div>
            <div>
              <label htmlFor="price-label" className={labelClass}>Price label</label>
              <p className={hintClass}>e.g. “$2/month” or “$20/year”. Leave empty for default.</p>
              <input
                id="price-label"
                type="text"
                value={config.priceLabel}
                onChange={(e) => update('priceLabel', e.target.value)}
                className={inputClass}
                placeholder="$2/month"
              />
            </div>
            <div>
              <label htmlFor="subscription-success-message" className={labelClass}>Subscription success message</label>
              <textarea
                id="subscription-success-message"
                rows={1}
                value={config.subscriptionSuccessMessage}
                onChange={(e) => update('subscriptionSuccessMessage', e.target.value)}
                className={inputClass}
              />
            </div>
          </div>
        </section>

        <section className={sectionClass}>
          <h2 className="mb-2 font-semibold text-gray-900 text-sm">Notifications</h2>
          <div className="space-y-3 max-w-xl">
            <div>
              <label htmlFor="notification-title-prefix" className={labelClass}>Notification title prefix</label>
              <p className={hintClass}>Prepended to in-app notification titles (e.g. app name).</p>
              <input
                id="notification-title-prefix"
                type="text"
                value={config.notificationTitlePrefix}
                onChange={(e) => update('notificationTitlePrefix', e.target.value)}
                className={inputClass}
                placeholder="LinkedIn Notes"
              />
            </div>
            <div className="flex items-center gap-2">
              <input
                id="email-sending-enabled"
                type="checkbox"
                checked={config.emailSendingEnabled === 'true'}
                disabled
                readOnly
                className="opacity-70 border-gray-300 rounded focus:ring-sky-500 text-sky-600 cursor-not-allowed"
              />
              <label htmlFor="email-sending-enabled" className={`${labelClass} text-gray-500`}>Email sending enabled</label>
            </div>
            <p className={hintClass}>When off, email notifications are not sent (e.g. password reset, alerts). Read-only.</p>
          </div>
        </section>
      </div>
    </div>
  );
};

export default AdminSettings;
